// The holiday animation: on a holiday of the user's traditions, its symbols drift down over the
// app the first time it is opened that day. Off with Settings → Holidays → decorations, never with reduced
// motion, never for solemn days (Yom Kippur). Built on React Native's own Animated.
import React, { useEffect, useMemo, useState } from 'react';
import { AccessibilityInfo, Animated, AppState, Easing, Platform, StyleSheet, useWindowDimensions, View } from 'react-native';
import { useSettings } from '../store/settings';
import { localDay } from '../logic/dates';
import { HOLIDAYS, holidaysNear } from '../logic/holidays';

export default function HolidayDecor() {
  const traditions = useSettings(s => s.traditions);
  const enabled = useSettings(s => s.holidayDecor);
  const played = useSettings(s => s.holidayAnimationPlayed);
  const set = useSettings(s => s.set);
  const [today, setToday] = useState(localDay());
  const [reduceMotion, setReduceMotion] = useState<boolean | null>(null);
  const [playing, setPlaying] = useState<string[] | null>(null);

  useEffect(() => {
    const sub = AppState.addEventListener('change', s => s === 'active' && setToday(localDay()));
    AccessibilityInfo.isReduceMotionEnabled().then(setReduceMotion, () => setReduceMotion(false));
    return () => sub.remove();
  }, []);

  const holiday = useMemo(
    () => (enabled ? holidaysNear(today, traditions, 0).find(h => h.daysUntil === 0 && HOLIDAYS[h.id].animate) : undefined),
    [enabled, today, traditions],
  );

  useEffect(() => {
    if (!holiday || reduceMotion !== false) return;
    const key = `${holiday.id}:${today}`;
    if (played === key) return;
    set({ holidayAnimationPlayed: key });
    setPlaying(HOLIDAYS[holiday.id].decor);
  }, [holiday, today, reduceMotion, played, set]);

  return playing ? <Shower symbols={playing} onDone={() => setPlaying(null)} /> : null;
}

function Shower({ symbols, onDone }: { symbols: string[]; onDone: () => void }) {
  const { width, height } = useWindowDimensions();
  const pieces = useMemo(
    () =>
      Array.from({ length: 18 }, (_, i) => ({
        symbol: symbols[i % symbols.length],
        left: Math.random() * Math.max(0, width - 40),
        size: 22 + Math.random() * 16,
        drift: (Math.random() - 0.5) * 90,
        spin: `${Math.round((Math.random() - 0.5) * 360)}deg`,
        delay: Math.random() * 1400,
        duration: 2800 + Math.random() * 1800,
        t: new Animated.Value(0),
      })),
    // Positions are picked once per shower.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  );

  useEffect(() => {
    const anim = Animated.parallel(
      pieces.map(p => Animated.timing(p.t, { toValue: 1, duration: p.duration, delay: p.delay, easing: Easing.linear, useNativeDriver: Platform.OS !== 'web' })),
    );
    anim.start(() => onDone());
    return () => anim.stop();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <View style={[StyleSheet.absoluteFill, { overflow: 'hidden', pointerEvents: 'none' }]} accessibilityElementsHidden importantForAccessibility="no-hide-descendants">
      {pieces.map((p, i) => (
        <Animated.Text
          key={i}
          style={{
            position: 'absolute',
            top: -60,
            left: p.left,
            fontSize: p.size,
            opacity: p.t.interpolate({ inputRange: [0, 0.8, 1], outputRange: [1, 1, 0] }),
            transform: [
              { translateY: p.t.interpolate({ inputRange: [0, 1], outputRange: [0, height + 120] }) },
              { translateX: p.t.interpolate({ inputRange: [0, 1], outputRange: [0, p.drift] }) },
              { rotate: p.t.interpolate({ inputRange: [0, 1], outputRange: ['0deg', p.spin] }) },
            ],
          }}>
          {p.symbol}
        </Animated.Text>
      ))}
    </View>
  );
}
